import React from 'react';
import { Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AI_DISCLOSURE_LABELS, type AiDisclosure } from '../../lib/api/cms';
import { cn } from '../../lib/utils';

/**
 * KI-Kennzeichnung der Mediathek — Art. 50 Abs. 4 KI-VO, anwendbar seit dem
 * 02.08.2026.
 *
 * Kennzeichnungspflichtig sind Medien, die real wirken könnten: KI-„Fotos" von
 * Unterrichtssituationen, synthetische Gesichter, fotorealistische Renderings.
 * Nicht erfasst sind offensichtlich fiktive Darstellungen (Comic, Fantasy,
 * Abstraktes) und geringfügige KI-Bearbeitung wie Licht- oder Farbkorrektur.
 *
 * Die Angabe hängt am Medium, nicht an der Fundstelle — die Website badged
 * darüber automatisch jedes Vorkommen desselben Bildes.
 */

const DIALOG_HINT =
  'Nur nötig, wenn das Medium echt wirken könnte (KI-„Foto", synthetisches Gesicht, ' +
  'fotorealistisches Rendering). Offensichtlich fiktive Illustrationen und reine ' +
  'Licht-/Farbkorrektur brauchen keine Kennzeichnung.';

/** Kleines Badge für Thumbnails in Mediathek und Bildauswahl. */
export const AiDisclosureBadge: React.FC<{
  value: AiDisclosure | null | undefined;
  className?: string;
}> = ({ value, className }) => {
  if (!value || value === 'none') return null;
  return (
    <span
      className={cn(
        'pointer-events-none absolute right-1 top-1 flex items-center gap-1 rounded-full',
        'bg-foreground/70 px-1.5 py-0.5 text-[10px] font-medium text-background',
        className,
      )}
      title={AI_DISCLOSURE_LABELS[value]}
    >
      <Sparkles className="h-2.5 w-2.5" />
      KI
    </span>
  );
};

/** Auswahl der Kennzeichnung, z. B. in der Medienkarte. */
export const AiDisclosureSelect: React.FC<{
  value: AiDisclosure;
  onChange: (value: AiDisclosure) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <Select value={value} onValueChange={(v) => onChange(v as AiDisclosure)} disabled={disabled}>
    <SelectTrigger size="sm" aria-label="KI-Kennzeichnung">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {(Object.keys(AI_DISCLOSURE_LABELS) as AiDisclosure[]).map((key) => (
        <SelectItem key={key} value={key}>
          {AI_DISCLOSURE_LABELS[key]}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * Frage direkt nach dem Upload.
 *
 * Bewusst als Pflichtschritt statt als Feld, das man später ausfüllen könnte:
 * Der einzige Moment, in dem sicher jemand weiß, woher ein Bild stammt, ist der
 * Upload. Die Vorbelegung ist „kein KI-Inhalt" — geraten wird nichts.
 */
export const AiDisclosureDialog: React.FC<{
  open: boolean;
  filename?: string;
  onChoose: (value: AiDisclosure) => void;
}> = ({ open, filename, onChoose }) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) onChoose('none'); }}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Mit KI erzeugt?</DialogTitle>
        <DialogDescription>
          {filename ? `„${filename}" — ` : ''}
          {DIALOG_HINT}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-2">
        <Button type="button" variant="outline" onClick={() => onChoose('none')}>
          Nein, kein KI-Inhalt
        </Button>
        <Button type="button" onClick={() => onChoose('ai_generated')}>
          Ja, vollständig KI-generiert
        </Button>
        <Button type="button" variant="outline" onClick={() => onChoose('ai_modified')}>
          Teilweise — mit KI bearbeitet
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
